import { Injectable } from '@angular/core';
import {Observable} from 'rxjs/Observable';
import {CommonConstant} from '../../app.constants';

interface CacheContent {
  expiry: number;
  value: any;
}

@Injectable()
export class CacheService {
  private cache: Map<string, CacheContent> = new Map<string, CacheContent>();
  private manuallyDeletedKeys: string[] = [
    'InfluencerGroupComparisions',
    'InfluencerGroupConditionOperators',
    'InfluencerGroupConditionTypes',
    CommonConstant.LIST_ADVERTISER_BELONG_TO_USER,
    CommonConstant.DISCOVER_SCROLL_TOP
  ];
  readonly DEFAULT_MAX_AGE: number = 300000;

  public _shouldRemoveCachedData = true;


  get shouldRemoveCachedData() {
    return this._shouldRemoveCachedData;
  }

  public enableCacheRemoving() {
    this._shouldRemoveCachedData = true;
  }

  public disableCacheRemoving(removingKeys?: string[]) {
    this._shouldRemoveCachedData = false;
    if (Array.isArray(removingKeys)) {
      removingKeys.forEach(key => {
        this.cache.delete(key);
      });
    }
  }

  public addManuallyDeletedKey(key: string) {
    if (key) {
      this.manuallyDeletedKeys.push(key);
    }
  }

  public clearManuallyDeletedKeys() {
    this.manuallyDeletedKeys = [];
  }

  /**
   * Gets the value from cache if the key is provided.
   * If no value exists in cache, then check if the same call exists
   */
  get(key: string, fallback?: Observable<any>, maxAge?: number): Observable<any> {

    if (this.hasValidCachedValue(key)) {
      return Observable.of(this.cache.get(key).value);
    }

    if (!maxAge) {
      maxAge = this.DEFAULT_MAX_AGE;
    }

    if (fallback && fallback instanceof Observable) {
      return fallback.do((value) => { this.set(key, value, maxAge); });
    } else {
      return Observable.of(null);
    }
  }

  /**
   * Sets the value with key in the cache
   */
  set(key: string, value: any, maxAge: number = this.DEFAULT_MAX_AGE): void {
    this.cache.set(key, { value: value, expiry: Date.now() + maxAge });
  }

  /**
   * Checks if the a key exists in cache
   */
  has(key: string): boolean {
    return this.cache.has(key);
  }

  /**
   * Clear cached value based on key if exists in cache
   */
  clear(key: string) {
    if (this.has(key)) {
      this.cache.delete(key);
    }
  }

  clearAll() {
    for (const key of Array.from( this.cache.keys())) {
      if (this.manuallyDeletedKeys.indexOf(key) < 0) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Checks if the key exists and has not expired.
   */
  public hasValidCachedValue(key: string): boolean {
    if (this.cache.has(key)) {
      if (this.cache.get(key).expiry < Date.now()) {
        this.cache.delete(key);
        return false;
      }
      return true;
    } else {
      return false;
    }
  }

}



// WEBPACK FOOTER //
// ./src/app/services/cache/cache.service.ts